import axios from 'axios';
import { Preferences } from '@capacitor/preferences';

import i18n from '@/i18n';

// Labels used when the API list is unavailable (first install offline). Keeps
// the language picker usable by falling back on the locales bundled in the app.
const FALLBACK_LABELS = {
  de: 'Deutsch',
  en: 'English',
  es: 'Español',
  it: 'Italiano',
  fr: 'Français',
  ja: '日本語',
  ko: '한국어',
  nl: 'Nederlands',
  pl: 'Język polski',
  pt: 'Português',
  ru: 'русский язык',
};

const state = () => ({
  // Enabled languages as returned by GET /languages?filter=light&enabled=true.
  items: [],
});

const mutations = {
  SET_ITEMS(state, payload) {
    state.items = Array.isArray(payload) ? payload : [];
  },
};

const getters = {
  // Only the languages that are both enabled server-side and actually bundled
  // in the app, so we never offer a locale that would fall back to another one.
  available(state) {
    const bundled = i18n.global.availableLocales;

    if (!state.items.length) {
      return bundled.map((code) => ({
        id: null,
        code,
        label: FALLBACK_LABELS[code] || code,
      }));
    }

    return state.items
      .filter((item) => bundled.includes(item.code))
      .map((item) => ({
        id: item.id,
        code: item.code,
        label: item.label || FALLBACK_LABELS[item.code] || item.code,
      }));
  },
  getIdByCode: (state) => (code) => {
    if (!code) return null;

    const language = state.items.find(
      (item) => item.code === String(code).toLowerCase()
    );

    return language ? language.id : null;
  },
  getCodeById: (state) => (id) => {
    const language = state.items.find((item) => item.id === id);

    return language ? language.code : null;
  },
};

const actions = {
  // Public route: called with plain axios so it also works on the login screen,
  // before any token is available.
  async fetch({ commit }) {
    try {
      const languages = (
        await axios.get(`${process.env.VUE_APP_API_URL}/languages`, {
          params: { filter: 'light', enabled: 'true' },
        })
      ).data;

      if (!Array.isArray(languages)) return;

      commit('SET_ITEMS', languages);

      await Preferences.set({
        key: 'languages',
        value: JSON.stringify(languages),
      });
    } catch (e) {
      // Offline or API down: the cached list, or the bundled fallback, is used.
    }
  },
  async load({ commit }) {
    const languages = await Preferences.get({ key: 'languages' });

    if (languages.value) {
      try {
        commit('SET_ITEMS', JSON.parse(languages.value));
      } catch (e) {
        await Preferences.remove({ key: 'languages' });
      }
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
